/**
 * 🔔 월간 안내 메뉴 — 시험발송(관리자) · 미리보기(누구나) (v2.215.0 · 2026-09-29)
 *
 * 매월 딜등록 메시지방에 세 번 나간다(토·일·공휴일이면 20·24일은 앞 업무일, 25일은 다음 업무일 · 오전 9시):
 *   20일 만료 안내(글) · 24일 검토 표(그림) · 25일 상태표(엑셀). 옛 「월요일 아침 알림」 단추를 이 메뉴가 대신한다.
 *
 * · 🔔 시험발송 — **방에 올라간다**(방 사람들이 다 본다 · 「[시험발송]」 표시). 관리자만(서버도 막는다).
 * · 👁 미리보기 — 방에 올리지 않고 **나만** 본다: 24일 표를 그림 창으로.
 *   누른 순간 기준으로 만든다 — 실제 24일에는 그날 기준으로 다시 만든다.
 *   (25일 엑셀 받기는 v2.219.0 부터 목록 위 [📥 만료예정 상태표] 단추 — DealStatusXlsxButton. 메뉴 속이라 눈에 안 띄었다.)
 * · 바깥을 누르면 닫힌다(메뉴라 잃을 입력이 없다) — 공용 ResetMenu 와 같은 방식(배경 막이 없음 · check:modal 무관).
 */

import { useEffect, useRef, useState } from 'react';
import {
  sendExpiryAlertTest, fetchMonthlyPreview, type NoticeKind,
} from '../lib/deal-registration-api';
import { openPdfPreview } from '../lib/pdf-preview';

const KIND_LABEL: Record<NoticeKind, string> = {
  d20: '20일 만료 안내 (글)',
  d24: '24일 검토 표 (그림)',
  d25: '25일 상태표 (엑셀)',
};

export function DealMonthlyNoticeMenu({
  isAdmin,
  onNotice,
}: {
  isAdmin: boolean;
  /** 결과 한 줄 — 페이지 위 알림 띠에 보인다 */
  onNotice: (msg: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } }; // 창까지 닫지 않게 (v2.231.0)
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const test = async (kind: NoticeKind): Promise<void> => {
    setOpen(false);
    if (!window.confirm(`딜등록 메시지방에 「${KIND_LABEL[kind]}」 시험발송을 올립니다.\n\n⚠ 그 방에 있는 분들이 모두 보게 됩니다.\n(맨 앞에 「[시험발송]」 이 붙습니다)\n\n올릴까요?`)) return;
    setBusy(kind);
    try {
      const r = await sendExpiryAlertTest(kind);
      if (!r.sent) { onNotice(`⚠ 시험발송을 못 했습니다 — ${r.reason ?? '알 수 없는 이유'}`); return; }
      const what = r.dealCount > 0 ? `${r.dealCount}건` : '대상 없음(닿는지만 확인)';
      onNotice(`🔔 딜등록 메시지방에 「${KIND_LABEL[kind]}」 시험발송을 올렸습니다 — ${what}. 메시지방에서 확인해 주세요.`);
    } catch (e) {
      onNotice(`⚠ 시험발송 실패 — ${(e as Error).message}`);
    } finally {
      setBusy('');
    }
  };

  const preview = async (): Promise<void> => {
    setOpen(false);
    setBusy('d24');
    try {
      const { blob, name } = await fetchMonthlyPreview('d24');
      const url = URL.createObjectURL(blob);
      // 그림은 포털 공용 미리보기 창으로(저장·새 탭 단추가 있다). 창이 닫힐 때까지 주소를 살려 둔다.
      openPdfPreview({ url, downloadUrl: url, filename: name, mime: 'image/png' });
    } catch (e) {
      onNotice(`⚠ 미리보기를 만들지 못했습니다 — ${(e as Error).message}`);
    } finally {
      setBusy('');
    }
  };

  const item = 'w-full text-left px-3 py-2 hover:bg-blue-50 disabled:opacity-40 flex flex-col gap-0.5';
  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy !== ''}
        aria-expanded={open}
        title="매월 20일 안내 · 24일 검토 표 · 25일 상태표 — 미리보기(나만) · 시험발송(방에 올라감 · 관리자)"
        className="btn btn-secondary"
      >
        {busy ? '만드는 중…' : '🔔 월간 안내'} <span className="text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-slate-300 rounded shadow-lg z-40 min-w-[300px] py-1 text-sm" data-deal-notice-menu>
          <div className="px-3 pt-1 pb-1.5 text-xs text-slate-500 leading-4">
            매월 20일 안내 · 24일 검토 표 · 25일 상태표가 딜등록 메시지방에 오전 9시에 나갑니다
            (토·일·공휴일이면 20·24일은 앞 업무일, 25일은 다음 업무일).
          </div>
          <div className="border-t border-slate-100" />
          <button type="button" className={item} onClick={() => { void preview(); }}>
            <span className="font-medium text-slate-800">👁 24일 검토 표 미리보기</span>
            <span className="text-xs text-slate-500">나만 봅니다 · 지금 자료로 만든 그림</span>
          </button>
          <div className="px-3 py-1.5 text-xs text-slate-500 leading-4">
            25일 상태표(엑셀)는 옆 <b className="text-amber-800">[📥 만료예정 상태표]</b> 단추로 언제든 지금 자료로 받습니다.
          </div>
          {isAdmin && (
            <>
              <div className="border-t border-slate-100 my-1" />
              <div className="px-3 pb-1 text-xs font-semibold text-amber-700">🔔 시험발송 — 메시지방에 올라갑니다 (방 사람들이 다 봅니다)</div>
              {(['d20', 'd24', 'd25'] as NoticeKind[]).map((k) => (
                <button key={k} type="button" className={item} onClick={() => { void test(k); }}>
                  <span className="font-medium text-slate-800">{KIND_LABEL[k]}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
